import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { Image, ImageProps, Video } from '~/shared/components';
import Button, { StyledButton } from '~/shared/components/Button';
import { Carousel, CarouselProps } from '~/shared/components/Carousel';
import carouselStyles from '~/shared/components/Carousel/Carousel.module.scss';
import { mq } from '~/lib/helpers/media-query.helper';
import Left from '~/icons/chevron-left.svg';
import Right from '~/icons/chevron.svg';
import Grid from '~/shared/components/Grid';

export type TMobilePDP = CarouselProps & {
    images?: ImageProps[];
    className?: string;
    videos?: string[];
    onClick?: (index: number) => void;
};

const ImageWrapper = styled.div(({ theme }) => ({
    position: 'relative',
    background: theme.colors.brownUltraLight,
    img: {
        mixBlendMode: 'multiply',
    },
}));

const CarouselWrap = styled.div({
    gridArea: '1/1',
    maxWidth: `calc(100vw - 4px)`,
});
const ButtonWrap = styled.div(() => ({
    gridArea: '1/1',
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
}));

const VideoWrapper = styled.div(() => ({}));
const renderImage = (image: ImageProps, idx: number, onClick?: (index: number) => void) => (
    <ImageWrapper>
        <Image
            sizes={`${mq(0, 'md')} 75vw`}
            objectFit="contain"
            width={3}
            height={2}
            priority
            src={image.src}
            onClick={onClick ? () => onClick(idx) : () => undefined}
        />
    </ImageWrapper>
);

const renderVideo = (videoUrl: string) => (
    <VideoWrapper>
        <div style={{ display: 'flex', height: '100%' }}>
            <Video src={videoUrl} />
        </div>
    </VideoWrapper>
);

export const MobilePDPCarousel = styled(({ images, className, onClick, videos }: TMobilePDP) => {
    const prevRef = useRef<HTMLButtonElement>(null);
    const nextRef = useRef<HTMLButtonElement>(null);
    const items = images?.map((image, idx) => renderImage(image, idx, onClick)) ?? [];
    const videoItems = videos?.map(renderVideo) ?? [];

    return (
        <div className={className}>
            <Grid flow="row">
                <CarouselWrap>
                    <Carousel
                        options={{
                            spaceBetween: 0,
                            slidesPerView: 1,
                            centeredSlides: true,
                        }}
                        items={[...items, ...videoItems]}
                        prevRef={prevRef}
                        nextRef={nextRef}
                    />
                </CarouselWrap>
                <ButtonWrap>
                    <Button
                        aria-label="Previous"
                        ref={prevRef}
                        variant="icon"
                        children={<Left />}
                    />
                    <Button aria-label="Next" ref={nextRef} variant="icon" children={<Right />} />
                </ButtonWrap>
            </Grid>
        </div>
    );
})(({ theme }) => ({
    width: '100%',
    [`.${carouselStyles.carouselContainer}`]: {
        height: '100%',
        aspectRatio: '3/2',
    },
    [`${StyledButton}`]: {
        zIndex: 1,
        '&:disabled,&:active,&:disabled:hover': {
            backgroundColor: 'transparent',

            color: theme.colors.brownLight,
        },
        '&:focus,&:hover': {
            backgroundColor: 'transparent',
        },
        svg: {
            width: 10,
        },
        'svg path': {
            fill: 'transparent',
        },
    },
}));
